/**
 * Format date/time using browser locale and timezone
 * Based on Intl.DateTimeFormat for internationalization
 */

export interface FormatDateTimeOptions {
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
  timeStyle?: 'full' | 'long' | 'medium' | 'short';
  locale?: string;
}

export function formatDateTime(
  date: string | Date | null | undefined,
  options: FormatDateTimeOptions = {}
): string {
  if (!date) {
    return '';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check for invalid date
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const { dateStyle = 'medium', timeStyle, locale } = options;

  try {
    const formatter = new Intl.DateTimeFormat(locale || navigator.language, {
      dateStyle,
      timeStyle
    });

    return formatter.format(dateObj);
  } catch (error) {
    console.error('Date formatting error:', error);
    // Fallback to ISO string
    return dateObj.toISOString();
  }
}

/**
 * Format date only (no time)
 */
export function formatDate(
  date: string | Date | null | undefined,
  options: Omit<FormatDateTimeOptions, 'timeStyle'> = {}
): string {
  return formatDateTime(date, { ...options, timeStyle: undefined });
}

/**
 * Format time only (no date)
 */
export function formatTime(
  date: string | Date | null | undefined,
  options: Omit<FormatDateTimeOptions, 'dateStyle'> = {}
): string {
  return formatDateTime(date, { ...options, dateStyle: undefined });
}

/**
 * Calculate time remaining until a future date
 * Returns a human-readable string showing days, hours and minutes remaining
 */
export function calculateTimeRemaining(
  untilDate: string | Date,
  options: { showDays?: boolean } = {}
): string {
  const now = new Date();
  const until = new Date(untilDate);
  const diffMs = until.getTime() - now.getTime();

  if (diffMs <= 0) {
    return '⚠️ EXPIRED - Awaiting auto-lift (runs hourly at :10)';
  }

  const diffMinutes = Math.floor(diffMs / 1000 / 60);
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  const parts: string[] = [];

  if (options.showDays && hours >= 24) {
    const days = Math.floor(hours / 24);
    parts.push(`${days}d`);
    const remainingHours = hours % 24;
    if (remainingHours > 0) {
      parts.push(`${remainingHours}h`);
    }
  } else if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  if (parts.length === 0) {
    return `${minutes}m`; // 1分未満の場合の "0m" 表示を維持
  }

  return parts.join(' ');
}
